<template>
    <left-side-filters
        module-name="newspaperMaterials"
        :filters="filters"
        @apply-filters="applyFilters"
    >
        <template #header>
            <bread-crumbs
                v-if="!activeMaterial"
                force-default-url
                default-url="my-business"
            />
            <bread-crumbs v-else stop @click.native="activeIdx = null" />
        </template>

        <template v-if="activeMaterial" #after-filters>
            <div class="pr-xl-3">
                <the-btn
                    class="mt-md-3 mb-3 mb-lg-0 w-100"
                    icon="Download"
                    :text="`Pobierz ${isAnyFilterApplied ? 'wybrane' : 'wszystkie'} materiały`"
                    :loading-status="loader ? 'loading' : 'idle'"
                    @click.native="downloadMaterials"
                />
            </div>
        </template>

        <template #content>
            <div class="c-settings pt-1" v-if="!activeMaterial">
                <sub-section-header title="Materiały gazetkowe"/>

                <div
                    class="c-settings-filters ml-auto"
                    style="order: 0 !important"
                >
                    <sort-component
                        text="Sortuj po:"
                        module-name="newspaperMaterials"
                        :options="
                            $store.getters.getNewspaperMaterialsSortOptions
                        "
                        @onChange="fetch"
                    />
                </div>
            </div>
            <div class="c-settings pt-1" v-else>
                <div>
                    <h4
                        class="typo typo--h2"
                        v-text="materialTitle(activeMaterial)"
                    />

                    <newspaper-date class="mt-2" :material="activeMaterial" />
                </div>
            </div>

            <selected-filters
                :filters="filters"
                class="mb-4"
                module-name="newspaperMaterials"
                @applyFilters="applyFilters"
            />

            <page-preloader v-if="status === 'loading'" />

            <div v-else-if="materials.length" class="newspaper-materials">
                <newspaper-details
                    v-if="activeMaterial"
                    :material="activeMaterial"
                    @update-filters="filteredFiles = $event"
                />

                <template v-else>
                    <newspaper-preview
                        v-for="(material, idx) in materials"
                        :material="material"
                        :key="`material-${idx}`"
                        @click.native="activeIdx = idx"
                    />
                </template>
            </div>

            <div v-else v-text="'Brak wyników'" />
        </template>
    </left-side-filters>
</template>

<script>
import LeftSideFilters from '@/components/layout/LeftSideFilters'
import NewspaperDetails from '@/components/my-business/marketing/NewspaperDetails'
import NewspaperPreview from '@/components/my-business/marketing/NewspaperPreview'
import NewspaperDate from '@/components/my-business/marketing/NewspaperDate'

export default {
    data() {
        return {
            activeIdx: null,
            filteredFiles: null,
            loader: false,
            queueTimer: null
        }
    },
    computed: {
        status() {
            return this.$store.getters.getNewspaperMaterialsStatus
        },

        materials() {
            return this.$store.getters.getNewspaperMaterials
        },

        materialsCategories() {
            if (!this.activeMaterial) {
                return []
            }

            let files = this.filteredFiles || this.activeMaterial.files

            // group files by type
            files = files.reduce((acc, item) => {
                if (!acc[item.type]) {
                    acc[item.type] = []
                }

                acc[item.type].push(item)
                return acc
            }, {})

            return Object.entries(files).map(([key, value]) => ({
                label: `${key} (${value.length})`,
                value: key
            }))
        },

        filters() {
            const filters = this.$store.getters.getNewspaperMaterialsFilters

            if (!this.activeMaterial) {
                // get filters that don't have noRefetch prop
                return Object.fromEntries(
                    Object.entries(filters).filter(
                        ([, value]) => !value.noRefetch
                    )
                )
            }

            // get filters that have noRefetch prop
            return Object.fromEntries(
                Object.entries(filters)
                    .filter(([, value]) => value.noRefetch)
                    .map(([key, value]) => {
                        if (key === 'category') {
                            return [
                                key,
                                {
                                    ...value,
                                    options: this.materialsCategories
                                }
                            ]
                        }

                        return [key, value]
                    })
            )
        },

        activeMaterial() {
            return this.materials[this.activeIdx]
        },

        isAnyFilterApplied() {
            if (!this.activeMaterial) {
                return false
            }

            return Object.values(this.filters).some(
                filter => filter.value.length
            )
        }
    },

    methods: {
        fetch() {
            this.activeIdx = null
            this.$store.dispatch('loadNewspaperMaterials')
        },

        applyFilters() {
            this.fetch()
        },

        materialTitle({ series, franchise, format }) {
            return `Cykl ${series} - ${franchise} ${format}`
        },

        async downloadMaterials() {
            if (this.loader) {
                return
            }

            this.loader = true

            const {
                format,
                franchise,
                series,
                public_to,
                public_from
            } = this.activeMaterial

            const res = await this.$store.$api.post(
                'newspaper_materials/download',
                {
                    format: [format],
                    franchise: [franchise],
                    series,
                    public_to,
                    public_from,
                    name: this.filters.filename.value || undefined,
                    subname: this.filters.subname.value.length
                        ? this.filters.subname.value
                        : undefined,
                    types: this.filters.category.value.length
                        ? this.filters.category.value
                        : undefined
                }
            )

            if (!res.status === 200) {
                return this.$store.dispatch('addNotification', {
                    type: 'danger',
                    content: 'Wystąpił błąd'
                })
            }

            const queueId = res.data.data.uuid

            const checkProgress = async () => {
                const res = await this.$store.$api.get(
                    `newspaper_materials/download/${queueId}/status`
                )

                return res.data.data
            }

            setTimeout(() => {
                startInterval()
            }, 1000)

            const startInterval = () => {
                this.queueTimer = setInterval(async () => {
                    const { file_name, file_url, state } = await checkProgress()

                    if (state === 'finished' || state === 'error') {
                        this.clearTimer()
                        this.loader = false

                        if (!file_name || state === 'error') {
                            return this.$store.dispatch('addNotification', {
                                type: 'danger',
                                content: 'Wystąpił błąd'
                            })
                        }

                        this.$functions.downloadFile(file_url, file_name)
                    }
                }, 1000)
            }
        },

        clearTimer() {
            clearInterval(this.queueTimer)
            this.queueTimer = null
        }
    },

    beforeDestroy() {
        this.clearTimer()
    },

    async created() {
        await this.$store.dispatch('extendedDataFetch', {
            includes: 'locations'
        })

        this.fetch()
    },

    watch: {
        activeIdx() {
            this.filteredFiles = null
        }
    },

    components: {
        LeftSideFilters,
        NewspaperDetails,
        NewspaperPreview,
        NewspaperDate
    }
}
</script>

<style lang="scss">
.newspaper-materials {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    justify-content: space-between;
}
</style>
